import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton,
  Paper, Stack, Chip, Backdrop, Alert, Snackbar, useTheme,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Tooltip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import Lottie from 'lottie-react';

// Icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

// Animations (Matches previous components)
import successAnimation from '../../assets/icons/sucess.json';
import loadingAnimation from '../../assets/icons/message-sending.json';

import axiosInstance from '../../api/axiosConfig';
import NoResultsImage from '../../assets/no-results.png';
import EventForm from './EventForm';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import PageContainer from '../common/PageContainer';
import EmptyState from '../common/EmptyState';
import {
  buildReportHtml,
  downloadTablePdf,
  escapeHtml,
  getSchoolReportProfile,
  openPrintPreview,
} from '../../utils/reportPrintUtils';

// --- STYLED COMPONENTS ---

const ListSurface = styled(AdminPanelCard)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableHead-root .MuiTableRow-root': {
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
    color: '#64748b',
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: '#f8fbff',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 'none',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
    borderLeft: 'none',
    borderRight: 'none',
    fontSize: '0.92rem',
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(1.6),
    paddingBottom: theme.spacing(1.6),
  },
}));

// --- Print Template Generator ---
const generatePrintHtml = (event, schoolDetails) => {
  const isNotice = event.isNotice;
  const eventType = isNotice ? 'Circular' : 'Event';
  const startDateFormatted = event.startDate ? format(new Date(event.startDate), 'MMM dd, yyyy') : 'N/A';
  const endDateFormatted = event.endDate ? format(new Date(event.endDate), 'MMM dd, yyyy') : 'N/A';
  const formattedDescription = (event.description || 'No description provided.')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />');

  const metaCards = [
    !isNotice
      ? `<div class="meta-card"><div class="label">Schedule</div><div class="value">${escapeHtml(startDateFormatted === endDateFormatted ? startDateFormatted : `${startDateFormatted} - ${endDateFormatted}`)}</div></div>`
      : '',
    event.location && !isNotice
      ? `<div class="meta-card"><div class="label">Location</div><div class="value">${escapeHtml(event.location)}</div></div>`
      : '',
    event.organizer && !isNotice
      ? `<div class="meta-card"><div class="label">Organizer</div><div class="value">${escapeHtml(event.organizer)}</div></div>`
      : '',
    `<div class="meta-card"><div class="label">Type</div><div class="value">${escapeHtml(eventType)}</div></div>`,
  ].filter(Boolean).join('');

  return buildReportHtml({
    title: `${eventType} Detail Sheet`,
    subtitle: event.title,
    school: {
      schoolName: schoolDetails.name,
      address: schoolDetails.address,
      contact: schoolDetails.contact,
    },
    body: `
      <div class="meta-grid">${metaCards}</div>
      <div class="meta-card" style="margin-bottom:18px;">
        <div class="label">Title</div>
        <div class="value">${escapeHtml(event.title)}</div>
      </div>
      <div class="meta-card">
        <div class="label">Description</div>
        <div class="value" style="font-size:12px; font-weight:500; line-height:1.7;">${formattedDescription}</div>
      </div>
      ${event.contactInfo ? `<div class="note">Contact: ${escapeHtml(event.contactInfo)}</div>` : ''}
    `,
  });
};

// --- MAIN COMPONENT ---
const EventListAndForm = () => {
  const theme = useTheme();
  const location = useLocation();
  const schoolId = Number(localStorage.getItem('schoolId')) || null;
  const tabParam = new URLSearchParams(location.search).get('tab') || '';
  const normalizedTab = String(tabParam).trim().toLowerCase();
  const isNoticeTab = ['circulars', 'circular', 'notices', 'notice'].includes(normalizedTab);

  const [events, setEvents] = useState([]);
  const[loading, setLoading] = useState(true);
  
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventData, setEventData] = useState({
    eventId: null, title: '', startDate: '', endDate: '', description: '', location: '', organizer: '', contactInfo: '',
  });

  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [animationState, setAnimationState] = useState('idle');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!schoolId) {
      setSnackbar({ open: true, message: 'Missing School ID. Please log in again.', severity: 'error' });
      setLoading(false); return;
    }
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, isNoticeTab]);

  const fetchEvents = async () => {
    setLoading(true); setEvents([]);
    try {
      const requestedIsNotice = isNoticeTab ? 1 : 0;
      const response = await axiosInstance.get('/api/events', { params: { schoolId, isNotice: requestedIsNotice } });
      const fetchedEvents = (response.data.events || response.data ||[]).map(event => ({
        ...event,
        isNotice: event.isNotice === 1 || event.isNotice === true,
        recordDate: event.createdAt ? format(new Date(event.createdAt), 'MMM dd, yyyy') : (event.startDate ? format(new Date(event.startDate), 'MMM dd, yyyy') : 'N/A'),
        startDate: event.startDate ? format(new Date(event.startDate), 'yyyy-MM-dd') : '',
        endDate: event.endDate ? format(new Date(event.endDate), 'yyyy-MM-dd') : '',
        description: event.description || '',
      })).filter((event) => requestedIsNotice === 1 ? event.isNotice === true : event.isNotice === false);
      setEvents(fetchedEvents);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to fetch data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    const { title, startDate, endDate, description, location, organizer, contactInfo } = formData;
    const isNoticeValue = isNoticeTab ? 1 : 0;
    const isNotice = isNoticeValue === 1;

    if (!title.trim()) {
      setSnackbar({ open: true, message: 'Please fill in the Title field.', severity: 'warning' }); return;
    }

    const eventPayload = {
      title: title.trim(),
      startDate: isNotice ? formData.startDate : startDate,
      endDate: isNotice ? formData.endDate : endDate,
      description: description || '',
      location: isNotice ? null : (location || null),
      organizer: isNotice ? null : (organizer || null),
      contactInfo: isNotice ? null : (contactInfo || null),
      isNotice: isNoticeValue,
      schoolId,
    };

    setFormDialogOpen(false);
    setAnimationState('saving');
    
    try {
      if (editingEventId) {
        await axiosInstance.put(`/api/events/${editingEventId}`, eventPayload);
        setEvents(prev => prev.map(evt => evt.eventId === editingEventId ? { ...evt, ...eventPayload } : evt));
      } else {
        const response = await axiosInstance.post('/api/events', eventPayload);
        const newEvent = { ...eventPayload, eventId: response.data.eventId || Date.now(), recordDate: format(new Date(), 'MMM dd, yyyy') };
        setEvents(prev =>[newEvent, ...prev]);
      }
      setAnimationState('success');
      setTimeout(() => {
        setSnackbar({ open: true, message: `${isNotice ? 'Circular' : 'Event'} saved successfully!`, severity: 'success' });
        resetForm(); setAnimationState('idle');
      }, 2000);
    } catch (error) {
      setAnimationState('error');
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save data', severity: 'error' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    setConfirmDeleteDialogOpen(false);
    setAnimationState('saving');
    try {
      await axiosInstance.delete(`/api/events/${eventToDelete.eventId}`, { params: { schoolId } });
      setEvents(prev => prev.filter(e => e.eventId !== eventToDelete.eventId));
      setAnimationState('success');
      setTimeout(() => {
        setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
        setEventToDelete(null); setAnimationState('idle');
      }, 2000);
    } catch (error) {
      setAnimationState('error');
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete', severity: 'error' });
    }
  };

  const resetForm = () => {
    setEventData({ eventId: null, title: '', startDate: '', endDate: '', description: '', location: '', organizer: '', contactInfo: '' });
    setEditingEventId(null);
  };

  const handleEdit = (event) => {
    setEventData({
      eventId: event.eventId, title: event.title || '', startDate: event.startDate ? format(new Date(event.startDate), 'yyyy-MM-dd') : '',
      endDate: event.endDate ? format(new Date(event.endDate), 'yyyy-MM-dd') : '', description: event.description || '',
      location: event.location || '', organizer: event.organizer || '', contactInfo: event.contactInfo || '',
    });
    setEditingEventId(event.eventId); setFormDialogOpen(true);
  };

  const handlePrint = (event) => {
    const schoolDetails = {
      name: getSchoolReportProfile().schoolName,
      address: getSchoolReportProfile().address,
      contact: getSchoolReportProfile().contact,
    };
    try {
      openPrintPreview({
        title: `${event.title} print`,
        html: generatePrintHtml(event, schoolDetails),
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Pop-up blocked. Allow pop-ups to print.', severity: 'warning' });
    }
  };

  const handleDownloadPdf = () => {
    const reportLabel = isNoticeTab ? 'Circular Register' : 'Event Register';
    downloadTablePdf({
      title: reportLabel,
      subtitle: `${events.length} ${eventTypeNamePlural.toLowerCase()} ready for archive or print`,
      filename: `${eventTypeNamePlural.toLowerCase().replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`,
      orientation: 'landscape',
      columns: ['#', eventTypeName, 'Schedule', 'Day', isNoticeTab ? 'Audience' : 'Location', 'Status'],
      rows: events.map((event, index) => {
        const isCompleted = !event.isNotice && event.endDate && new Date(event.endDate) < now;
        const primaryDate = isNoticeTab ? (event.recordDate || event.startDate || event.createdAt) : event.startDate;
        const scheduleLabel = isNoticeTab
          ? formatDateValue(primaryDate)
          : event.startDate && event.endDate && event.startDate !== event.endDate
            ? `${formatDateValue(event.startDate)} - ${formatDateValue(event.endDate)}`
            : formatDateValue(event.startDate);
        return [
          index + 1,
          event.title || '-',
          scheduleLabel,
          getDayName(primaryDate),
          isNoticeTab ? 'School Wide' : (event.location || '-'),
          isCompleted ? 'Completed' : 'Active',
        ];
      }),
    });
    setSnackbar({ open: true, message: `${reportLabel} downloaded as PDF.`, severity: 'success' });
  };

  const formatDateValue = (val) => val ? format(new Date(val), 'dd MMM yyyy') : 'N/A';
  const getDayName = (val) => val ? format(new Date(val), 'EEE') : '-';

  const eventTypeName = isNoticeTab ? 'Circular' : 'Event';
  const eventTypeNamePlural = isNoticeTab ? 'Circulars' : 'Events';
  const heroTitle = isNoticeTab ? 'Circulars Hub' : 'Events Hub';
  const heroSubtitle = isNoticeTab
    ? 'Publish school circulars and important notices from one workspace.'
    : 'Create, organize, and publish school events from one workspace.';
  const now = new Date();
  const upcomingCount = events.filter((event) => isNoticeTab || !event.endDate || new Date(event.endDate) >= now).length;
  const completedCount = events.filter((event) => !isNoticeTab && event.endDate && new Date(event.endDate) < now).length;

  return (
    <PageContainer>
      
      <AdminPageHero
        title={heroTitle}
        subtitle={heroSubtitle}
        metrics={[
          { label: 'Total', value: events.length },
          { label: 'Active', value: upcomingCount },
          ...(!isNoticeTab ? [{ label: 'Completed', value: completedCount }] : []),
        ]}
        actions={(
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" color="inherit" startIcon={<PrintRoundedIcon />} onClick={() => events.length > 0 ? handlePrint(events[0]) : setSnackbar({ open: true, message: `No ${eventTypeNamePlural.toLowerCase()} to print.`, severity: 'warning' })} sx={{ borderRadius: 1.5, fontWeight: 700, borderColor: alpha(theme.palette.divider, 0.8) }}>
              Print Lead Sheet
            </Button>
            <Button variant="outlined" color="inherit" startIcon={<PrintRoundedIcon />} onClick={handleDownloadPdf} disabled={events.length === 0} sx={{ borderRadius: 1.5, fontWeight: 700, borderColor: alpha(theme.palette.divider, 0.8) }}>
              Download PDF
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={() => { resetForm(); setFormDialogOpen(true); }} sx={{ py: 1, borderRadius: 1.5, fontWeight: 800 }}>
              Add {eventTypeName}
            </Button>
          </Stack>
        )}
      />

      {/* CONTENT LIST */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh"><CircularProgress /></Box>
      ) : events.length === 0 ? (
        <EmptyState
          title={`No ${eventTypeNamePlural} Found`}
          description={`Click "Add ${eventTypeName}" to publish the first one.`}
          icon={<img src={NoResultsImage} alt="No Results" style={{ maxWidth: '160px', opacity: 0.8 }} />}
          action={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { resetForm(); setFormDialogOpen(true); }} sx={{ mt: 2, borderRadius: 1.5, fontWeight: 800 }}>Add {eventTypeName}</Button>}
        />
      ) : (
        <ListSurface
          title={`${eventTypeNamePlural} List`}
          subtitle={`Review published ${eventTypeNamePlural.toLowerCase()}, update details, and manage status.`}
          contentSx={{ p: 0 }}
          sx={{ mb: 4 }}
        >
          <SoftTableContainer>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>#</TableCell>
                <TableCell>{eventTypeName}</TableCell>
                <TableCell>{isNoticeTab ? 'Published' : 'Schedule'}</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>{isNoticeTab ? 'Audience' : 'Location'}</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, index) => {
                const isCompleted = !event.isNotice && event.endDate && new Date(event.endDate) < now;
                const primaryDate = isNoticeTab ? (event.recordDate || event.startDate || event.createdAt) : event.startDate;
                const scheduleLabel = isNoticeTab
                  ? formatDateValue(primaryDate)
                  : event.startDate && event.endDate && event.startDate !== event.endDate
                  ? `${formatDateValue(event.startDate)} - ${formatDateValue(event.endDate)}`
                  : formatDateValue(event.startDate);
                const descriptionText = event.description?.trim() || '-';

                return (
                  <TableRow key={event.eventId} hover>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {event.title}
                        </Typography>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={isNoticeTab ? 'Circular' : 'Event'}
                            sx={{
                              borderColor: alpha(isNoticeTab ? theme.palette.info.main : theme.palette.primary.main, 0.35),
                              color: isNoticeTab ? theme.palette.info.main : theme.palette.primary.main,
                              backgroundColor: alpha(isNoticeTab ? theme.palette.info.main : theme.palette.primary.main, 0.08),
                            }}
                          />
                          {event.organizer && !isNoticeTab && (
                            <Chip size="small" variant="outlined" label={event.organizer} />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>{scheduleLabel}</TableCell>
                    <TableCell>{getDayName(primaryDate)}</TableCell>
                    <TableCell>{isNoticeTab ? 'School Wide' : (event.location || '-')}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={descriptionText === '-' ? '' : descriptionText}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'normal',
                          }}
                        >
                          {descriptionText}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={isCompleted ? 'Completed' : 'Active'}
                        sx={{
                          minWidth: 84,
                          borderColor: isCompleted
                            ? alpha(theme.palette.text.secondary, 0.3)
                            : alpha(theme.palette.success.main, 0.35),
                          color: isCompleted ? 'text.secondary' : theme.palette.success.main,
                          backgroundColor: isCompleted
                            ? alpha(theme.palette.text.secondary, 0.06)
                            : alpha(theme.palette.success.main, 0.08),
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(event)}>
                          <EditRoundedIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print">
                        <IconButton size="small" onClick={() => handlePrint(event)}>
                          <PrintRoundedIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => { setEventToDelete(event); setConfirmDeleteDialogOpen(true); }}>
                          <DeleteRoundedIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SoftTableContainer>
        </ListSurface>
      )}

      {/* FORM DIALOG (Houses EventForm component) */}
      <Dialog open={formDialogOpen} onClose={() => { setFormDialogOpen(false); resetForm(); }} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          {editingEventId ? `Edit ${eventTypeName}` : `Add New ${eventTypeName}`}
          <IconButton onClick={() => { setFormDialogOpen(false); resetForm(); }} sx={{ position: 'absolute', right: 16, top: 16 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <EventForm
            eventData={eventData} setEventData={setEventData}
            handleSubmit={(e) => { e.preventDefault(); handleFormSubmit(eventData); }}
            handleCancel={() => { setFormDialogOpen(false); resetForm(); }}
            submitting={animationState === 'saving'}
            isNotice={isNoticeTab} isEdit={!!editingEventId}
          />
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, p: 1, minWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1 }}>Confirm Deletion</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1">Are you sure you want to delete <strong>"{eventToDelete?.title}"</strong>? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setConfirmDeleteDialogOpen(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" sx={{ px: 4, borderRadius: 2, fontWeight: 700 }}>Yes, Delete</Button>
        </DialogActions>
      </Dialog>

      {/* FULLSCREEN ANIMATION OVERLAY */}
      <Backdrop open={animationState !== 'idle'} sx={{ zIndex: 9999, flexDirection: 'column', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' }}>
        <Box sx={{ textAlign: 'center' }}>
          {animationState === 'saving' && (
            <><Lottie animationData={loadingAnimation} style={{ width: 250, height: 250, margin: '0 auto' }} /><Typography variant="h5" fontWeight={800} color="primary" mt={-2}>Processing...</Typography></>
          )}
          {animationState === 'success' && (
            <><Lottie animationData={successAnimation} loop={false} style={{ width: 250, height: 250, margin: '0 auto' }} /><Typography variant="h5" fontWeight={800} color="success.main" mt={-2}>Success!</Typography></>
          )}
          {animationState === 'error' && (
            <><ErrorRoundedIcon color="error" sx={{ fontSize: 100, mb: 2 }} /><Typography variant="h5" fontWeight={800} color="error.main">Action Failed</Typography>
            <Typography variant="body1" color="text.secondary" mt={1} mb={3}>{snackbar.message}</Typography>
            <Button variant="contained" color="primary" onClick={() => setAnimationState('idle')} sx={{ borderRadius: 2, fontWeight: 700 }}>Dismiss</Button></>
          )}
        </Box>
      </Backdrop>

      {/* SNACKBAR */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 1.5, fontWeight: 600 }}>{snackbar.message}</Alert>
      </Snackbar>

    </PageContainer>
  );
};

export default EventListAndForm;
